export interface CountryDisplay {
  flag: string;
  label: string;
  code: string | null;
}

const UNKNOWN = '(unknown)';

let nameToCodeCache: Map<string, string> | null = null;

/** Flag emoji + readable label for a stored country value (ISO code or name). */
export function countryDisplay(raw: string | null | undefined): CountryDisplay {
  const trimmed = (raw ?? '').trim();
  if (!trimmed || trimmed === UNKNOWN) {
    return { flag: '', label: trimmed || UNKNOWN, code: null };
  }

  const code = resolveCountryCode(trimmed);
  if (!code) {
    return { flag: '', label: trimmed, code: null };
  }

  return {
    flag: countryCodeToFlagEmoji(code),
    label: formatCountryLabel(trimmed, code),
    code,
  };
}

/** Chart/doughnut legend label with leading flag when available. */
export function formatCountryChartLabel(raw: string): string {
  const d = countryDisplay(raw);
  return d.flag ? `${d.flag} ${d.label}` : d.label;
}

function resolveCountryCode(raw: string): string | null {
  if (/^[a-zA-Z]{2}$/.test(raw)) {
    return raw.toUpperCase();
  }
  return nameToCodeMap().get(normalizeCountryKey(raw)) ?? null;
}

function formatCountryLabel(raw: string, code: string): string {
  if (/^[a-zA-Z]{2}$/.test(raw)) {
    return regionName(code) ?? code;
  }
  return raw;
}

function regionName(code: string): string | null {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? null;
  } catch {
    return null;
  }
}

function countryCodeToFlagEmoji(code: string): string {
  const upper = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) {
    return '';
  }
  const base = 0x1f1e6;
  return [...upper]
    .map((ch) => String.fromCodePoint(base + ch.charCodeAt(0) - 65))
    .join('');
}

function normalizeCountryKey(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

function nameToCodeMap(): Map<string, string> {
  if (nameToCodeCache) {
    return nameToCodeCache;
  }

  const map = new Map<string, string>();
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    for (const code of regionCodes()) {
      const name = dn.of(code);
      if (name && name !== code) {
        map.set(normalizeCountryKey(name), code);
      }
    }
  } catch {
    // Intl unavailable — ISO codes still work via direct match.
  }

  for (const [name, code] of COMMON_COUNTRY_ALIASES) {
    map.set(normalizeCountryKey(name), code);
  }

  nameToCodeCache = map;
  return map;
}

function regionCodes(): string[] {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  if (typeof intl.supportedValuesOf === 'function') {
    return intl.supportedValuesOf('region');
  }
  return FALLBACK_REGION_CODES;
}

const COMMON_COUNTRY_ALIASES: readonly [string, string][] = [
  ['USA', 'US'],
  ['U.S.A.', 'US'],
  ['UK', 'GB'],
  ['Great Britain', 'GB'],
];

/** Subset used when Intl.supportedValuesOf is unavailable. */
const FALLBACK_REGION_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX',
  'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ',
  'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK',
  'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM',
  'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
  'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS',
  'GT', 'GU', 'GW', 'GY', 'HK', 'HM', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN',
  'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN',
  'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV',
  'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ',
  'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI',
  'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM',
  'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC',
  'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV',
  'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR',
  'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
];
